import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import _ from 'lodash';
import moment from 'moment';
import { numberFormat } from 'utils/app.utils';
import { UseSnCode } from 'utils/useSnCode';
import { forceTextOnDateCells } from 'utils/excel.utils';

export interface DoctorDetail {
    doctorId: number;
    doctorFullname: string;
    timeStart: string;    // "HH:mm"
    timeEnd: string;      // "HH:mm"
    countAppointment: number;
    countTreatment: number;
    handInsurance: number;
}

export interface DfRow {
    doctorId: number;
    // โหมด day
    cnNumber?: string;
    branchCnNumber?: string;
    snCode?: string;
    patientFullname?: string;
    operativeName?: string;
    isLab?: boolean;
    total?: number;
    paymentChannel?: string;
    df?: number;
    dfNet?: number;
    // โหมด month
    date?: string;
    appointments?: number;
    notYet?: number;
    walkIn?: number;
    creditCost?: number;
    handInsurance?: number;
    treatmentRight?: number
}

export interface ReportData {
    branchName: string;
    date: string;            // วันหรือเดือน
    doctorFullname: string;
    display: 'day' | 'month';
    dfData: any;
    t: (key: string) => string;
}

export async function ExportDfToExcel(reportData: ReportData): Promise<void> {
    const sn = await UseSnCode();
    const { branchName, date, doctorFullname, display, dfData, t } = reportData;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(t('REPORT.REPORT_NAME.DF_REPORT'), {
        pageSetup: { paperSize: 9, orientation: 'landscape' }
    });

    // 1) กำหนดความกว้างคอลัมน์ ตามโหมด
    const colsDay = [
        { width: 8 }, { width: 12 }, { width: 12 },
        { width: 12 }, { width: 20 }, { width: 25 },
        { width: 8 }, { width: 12 }, { width: 25 },
        { width: 14 }, { width: 12 }
    ];
    const colsMonth = [
        { width: 8 }, { width: 12 }, { width: 14 },
        { width: 8 }, { width: 8 }, { width: 8 },
        { width: 10 }, { width: 12 }, { width: 12 },
        { width: 14 }, { width: 14 }, { width: 12 },
        { width: 12 }, { width: 12 }
    ];
    ws.columns = display === 'day' ? [...colsDay] : [...colsMonth];
    // ถ้าไม่มี SN code ใน day mode ให้ลบคอลัมน์ D (index 3)
    if (!sn && display === 'day') {
        ws.columns.splice(3, 1);
    }
    const totalCols = ws.columns.length;

    let cursor = 1;

    // 2) หัวเรื่องหลัก
    ws.mergeCells(cursor, 1, cursor, totalCols);
    ws.getCell(cursor, 1).value = t('REPORT.REPORT_NAME.DF_REPORT');
    ws.getCell(cursor, 1).font = { size: 16, bold: true };
    ws.getCell(cursor, 1).alignment = { horizontal: 'center', vertical: 'middle' };
    cursor++;

    // 3) แถวสาขา / วันที่ / ทันตแพทย์
    ws.getCell(cursor, 1).value = t('REPORT.FILTER.BRANCH') + ':';
    ws.getCell(cursor, 1).font = { bold: true };
    ws.mergeCells(cursor, 2, cursor, 4);
    ws.getCell(cursor, 2).value = branchName;

    ws.getCell(cursor, 5).value = display === 'day'
        ? t('REPORT.FILTER.DATE') + ':'
        : t('REPORT.FILTER.MONTH') + ':';
    ws.getCell(cursor, 5).font = { bold: true };
    ws.mergeCells(cursor, 6, cursor, 7);
    ws.getCell(cursor, 6).value = display === 'day'
        ? moment(date).format('DD/MM/YYYY')
        : moment(date).format('MMMM YYYY');

    ws.getCell(cursor, 8).value = t('REPORT.FILTER.DOCTOR') + ':';
    ws.getCell(cursor, 8).font = { bold: true };
    ws.mergeCells(cursor, 9, cursor, totalCols);
    ws.getCell(cursor, 9).value = doctorFullname;
    cursor += 2;

    // 4) สร้างรายชื่อหมอ (doctorsList) ตามโหมด
    type DocInfo = {
        doctorId: number;
        doctorFullname: string;
        timeStart?: string;
        timeEnd?: string;
        countAppointment?: number;
        countTreatment?: number;
        handInsurance?: number;
        dfRows: DfRow[];
    };
    let doctorsList: any = [];

    if (display === 'day') {
        // ใช้ rowDetail เดิม
        doctorsList = dfData.rowDetail.map((doc: DoctorDetail) => ({
            doctorId: doc.doctorId,
            doctorFullname: doc.doctorFullname,
            timeStart: doc.timeStart,
            timeEnd: doc.timeEnd,
            countAppointment: doc.countAppointment,
            countTreatment: doc.countTreatment,
            handInsurance: doc.handInsurance,
            dfRows: dfData.rowDf.filter((r: DfRow) => r.doctorId === doc.doctorId)
        }));
    } else {
        // สร้างจาก rowDf group by doctorId
        doctorsList = _.chain(dfData.rowDf)
            .groupBy('doctorId')
            .map((rows: DfRow[], key: string) => ({
                doctorId: Number(key),
                doctorFullname: doctorFullname || '',
                dfRows: rows
            }))
            .value();
    }

    // 5) ไล่แต่ละหมอใน doctorsList
    for (const info of doctorsList) {
        const {
            doctorFullname: docName,
            timeStart,
            timeEnd,
            countAppointment,
            countTreatment,
            handInsurance,
            dfRows
        } = info;

        // 5.1 Sub-header ชื่อหมอ
        ws.mergeCells(cursor, 1, cursor, totalCols);
        ws.getCell(cursor, 1).value = `${t('REPORT.FILTER.DOCTOR')} ${docName}`;
        ws.getCell(cursor, 1).font = { size: 14, bold: true };
        cursor++;

        // 5.2 แถวเวลางานเฉพาะ day mode
        if (display === 'day') {
            const workTimeStr = (timeStart && timeEnd)
                ? `${moment(timeStart, 'HH:mm').format('HH:mm')} - ${moment(timeEnd, 'HH:mm').format('HH:mm')} น.`
                : '-';
            const rowWork = ws.addRow([
                t('REPORT.TITLE.TME_WORK'), workTimeStr,
                t('REPORT.TITLE.COUNT_APPOINTMENT'), countAppointment ? `${countAppointment} ${t('REPORT.TITLE.APPOINTMENT_UNIT')}` : '-',
                t('REPORT.TITLE.COUNT_TREATMENT'), countTreatment ? `${countTreatment} ${t('REPORT.TITLE.PEOPLE_UNIT')}` : '-',
                t('REPORT.TITLE.COUNT_HAND_INSURANCE'), handInsurance ? `${handInsurance} ${t('REPORT.TITLE.HAND_INSURANCE_UNIT')}` : '-'
            ]);
            [1, 3, 5, 7].forEach(ci => {
                const c = rowWork.getCell(ci);
                c.font = { bold: true };
                c.alignment = { vertical: 'middle', horizontal: 'left' };
            });
            cursor++;
        }

        // 5.3 หัวตาราง
        const headers = display === 'day'
            ? [
                t('REPORT.TABLE.DF.#NO'),
                t('REPORT.TABLE.DF.CN'),
                t('REPORT.TABLE.DF.BRANCH_CN'),
                t('REPORT.TABLE.DF.SN_CODE'),
                t('REPORT.TABLE.DF.PATIENT_NAME'),
                t('REPORT.TABLE.DF.OPERATIVE_LIST'),
                t('REPORT.TABLE.DF.IS_LAB'),
                t('REPORT.TABLE.DF.TRUE_PRICE'),
                t('REPORT.TABLE.DF.PAYMENT_CHANEL'),
                t('REPORT.TABLE.DF.TREATMENT_NET'),
                t('REPORT.TABLE.DF.DF3')
            ]
            : [
                t('REPORT.TABLE.DF.#NO'),
                t('REPORT.TABLE.DF.DATE'),
                t('REPORT.TABLE.DF.TIME'),
                t('REPORT.TABLE.DF.APPOINTMENT'),
                t('REPORT.TABLE.DF.WRONG_APPOINTMENT'),
                t('REPORT.TABLE.DF.WALK_IN'),
                t('REPORT.TABLE.DF.SUM_APPOINTMENT'),
                t('REPORT.TABLE.DF.PRICE'),
                t('REPORT.TABLE.DF.DF4'),
                t('REPORT.TABLE.DF.CREDIT_NET'),
                t('REPORT.TABLE.DF.TREATMENT_NET'),
                t('REPORT.TABLE.DF.DF5'),
                t('REPORT.TABLE.DF.HAND'),
                t('REPORT.TABLE.DF.DF6')
            ];
        if (!sn && display === 'day') headers.splice(3, 1);
        const rowHeader = ws.addRow(headers);
        rowHeader.eachCell(cell => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });
        cursor++;

        // 5.4 ถ้าไม่มีข้อมูล หรือยอดรวม = 0
        const totalSum = dfRows.reduce((s: any, x: any) => s + ((x.total || 0)), 0);
        if (dfRows.length === 0 || (totalSum === 0 && display === 'day')) {
            ws.mergeCells(cursor, 1, cursor, totalCols);
            ws.getCell(cursor, 1).value = t('REPORT.TITLE.NO_DATA');
            ws.getCell(cursor, 1).font = { italic: true };
            ws.getCell(cursor, 1).alignment = { horizontal: 'center' };
            cursor += 2;
            continue;
        }

        // 5.5 แสดงแต่ละแถว
        dfRows.forEach((d: any, i: any) => {
            let values: any[];
            if (display === 'day') {
                values = [
                    i + 1,
                    d.cnNumber,
                    d.branchCnNumber,
                    d.snCode ?? '-',
                    d.patientFullname,
                    d.operativeName,
                    d.isLab ? t('REPORT.UNIT.HAS') : t('REPORT.UNIT.NOT_HAS'),
                    d.total,
                    d.paymentChannel,
                    d.df,
                    d.dfNet
                ];
                if (!sn) values.splice(3, 1);
            } else {
                let workingTime = '-'
                if (d.timeStart && d.timeEnd) {
                    const timeStartSplit = d.timeStart.split(':')
                    const timeEndSplit = d.timeEnd.split(':')
                    workingTime = timeStartSplit[0] + ':' + timeStartSplit[1] + ' - ' + timeEndSplit[0] + ':' + timeEndSplit[1]
                }
                values = [
                    i + 1,
                    moment(d.date).format('DD/MM/YYYY'),
                    workingTime,
                    d.appointments || '0',
                    d.notYet || '0',
                    d.walkIn || '0',
                    (d.walkIn || 0) + (d.appointments || 0),
                    numberFormat(d.total || 0),
                    numberFormat(d.df || 0),
                    numberFormat(d.creditCost || 0),
                    numberFormat(d.treatmentRight || 0),
                    numberFormat(d.dfNet || 0),
                    numberFormat(d.handInsurance || 0),
                    d.dfNet > d.handInsurance ? numberFormat(d.dfNet || 0, 3, 3) : numberFormat(d.handInsurance || 0, 0, 3)
                ];
            }
            const row = ws.addRow(values);
            row.eachCell((cell, col) => {
                if (display === 'day') {
                    if ([1, 7].includes(col)) cell.alignment = { horizontal: 'center' };
                    if ([8, 10, 11].includes(col)) cell.alignment = { horizontal: 'right' };
                } else {
                    if ([1, 4, 5, 6].includes(col)) cell.alignment = { horizontal: 'center' };
                    if ([7, 8, 9, 10, 11, 12, 13, 14].includes(col)) cell.alignment = { horizontal: 'right' };
                }
            });
        });
        cursor += dfRows.length;

        // 5.6 สรุปยอด
        if (display === 'day') {
            const sumTotal = dfRows.reduce((s: any, x: any) => s + (x.total || 0), 0);
            const sumDf = dfRows.reduce((s: any, x: any) => s + (x.df || 0), 0);
            const sumDfNet = dfRows.reduce((s: any, x: any) => s + (x.dfNet || 0), 0);

            ws.mergeCells(cursor, 1, cursor, !sn ? 6 : 7);
            ws.getCell(cursor, 1).value = t('REPORT.TITLE.SUM');
            ws.getCell(cursor, 1).font = { bold: true };
            ws.getCell(cursor, 1).alignment = { horizontal: 'right' };
            ws.getCell(cursor, !sn ? 7 : 8).value = sumTotal;
            ws.getCell(cursor, !sn ? 9 : 10).value = sumDf;
            ws.getCell(cursor, !sn ? 10 : 11).value = sumDfNet;
            for (const ci of [!sn ? 7 : 8, !sn ? 9 : 10, !sn ? 10 : 11]) {
                ws.getCell(cursor, ci).font = { bold: true };
                ws.getCell(cursor, ci).alignment = { horizontal: 'right' };
            }

            // // merge A..E
            // ws.mergeCells(cursor, 1, cursor, 5);
            // ws.getCell(cursor, 1).value = t('REPORT.TITLE.SUM');
            // ws.getCell(cursor, 1).font = { bold: true };
            // ws.getCell(cursor, 1).alignment = { horizontal: 'right' };

            // //เขียน summary ที่ F,G,H
            // [sumTotal, sumDf, sumDfNet].forEach((val, idx) => {
            //     const ci = 6 + idx; // F=6,G=7,H=8
            //     const c = ws.getCell(cursor, ci);
            //     c.value = val;
            //     c.font = { bold: true };
            //     c.alignment = { horizontal: 'right' };
            // });

        } else {
            const sumNum = (key: keyof DfRow) =>
                dfRows.reduce((s: any, x: any) => s + ((x[key] as number) || 0), 0);

            // merge A..G
            ws.mergeCells(cursor, 1, cursor, 7);
            ws.getCell(cursor, 1).value = t('REPORT.TITLE.SUM');
            ws.getCell(cursor, 1).font = { bold: true };
            ws.getCell(cursor, 1).alignment = { horizontal: 'right' };
            const sumMax = dfRows.reduce(
                (s: any, o: any) => s + Math.max(o.dfNet || 0, o.handInsurance || 0),
                0
            );
            const summary = [
                t('REPORT.TITLE.SUM'),
                numberFormat(sumNum('total')),
                numberFormat(sumNum('df')),
                numberFormat(sumNum('creditCost'), 3, 3),
                numberFormat(sumNum('treatmentRight'), 3, 3),
                numberFormat(sumNum('dfNet')),
                numberFormat(sumNum('handInsurance')),
                numberFormat(sumMax, 3, 3)
            ];

            for (let idx = 0; idx < summary.length; idx++) {
                const ci = 7 + idx;
                const c = ws.getCell(cursor, ci);
                c.value = summary[idx];
                c.font = { bold: true };
                c.alignment = { horizontal: 'right' };
            }
        }

        cursor += 2;
    }

    // 6) สร้างไฟล์และดาวน์โหลด
    forceTextOnDateCells(wb);
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    saveAs(blob, `${t('REPORT.REPORT_NAME.DF_REPORT')}_${moment().format('YYYYMMDD')}.xlsx`);
}

